import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type GetForecastRollUpResult,
  type ForecastSubmission,
} from '@whinerp/client';
import { Button, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount, formatMoney } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * Submitting a forecast, and what a reporting line has submitted (G-142).
 *
 * The submitted number and the manager's adjustment sit side by side and never
 * merge. A rep who sees only the adjusted figure cannot tell their number was
 * changed; a manager who sees only their own cannot remember what they were
 * told. So a row that has been adjusted shows both, with who adjusted it and
 * why.
 *
 * The roll-up's most important line is the one about who has NOT submitted.
 * Two of five reps submitting makes a total that looks exactly like five of
 * five, and whoever reads it out in a meeting will not know which they have.
 */

export function ForecastSubmitPanel({
  periodKind,
  periodStart,
  periodEnd,
  periodLabel,
  currency,
}: {
  periodKind: 'month' | 'quarter' | 'year';
  periodStart: string;
  periodEnd: string;
  periodLabel: string;
  currency: string;
}): JSX.Element {
  const t = useT();
  const format = useFormat();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [adjusting, setAdjusting] = useState<string | null>(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');

  const manages = can('forecast.view.team');

  const rollUp = useQuery<GetForecastRollUpResult, ApiError>({
    queryKey: ['forecast-rollup', periodStart, periodEnd],
    queryFn: () => api.getForecastRollUp({ query: { periodStart, periodEnd } }),
    enabled: manages,
  });

  const refresh = (): Promise<unknown> =>
    queryClient.invalidateQueries({ queryKey: ['forecast-rollup'] });

  const submit = useMutation<ForecastSubmission, ApiError>({
    mutationFn: () =>
      api.submitForecast({
        body: {
          periodKind,
          periodStart,
          periodEnd,
          amount: amount.trim(),
          note: note.trim() === '' ? null : note.trim(),
        },
      }),
    onSuccess: async () => {
      setNote('');
      await refresh();
    },
  });

  const adjust = useMutation<ForecastSubmission, ApiError, string>({
    mutationFn: (userId) =>
      api.adjustForecast({
        body: { userId, periodStart, periodEnd, amount: adjustAmount.trim(), note: adjustNote.trim() },
      }),
    onSuccess: async () => {
      setAdjusting(null);
      setAdjustAmount('');
      setAdjustNote('');
      await refresh();
    },
  });

  const unadjust = useMutation<ForecastSubmission, ApiError, string>({
    mutationFn: (userId) =>
      api.clearForecastAdjustment({ query: { userId, periodStart, periodEnd } }),
    onSuccess: () => refresh(),
  });

  const money = (value: string): string => formatMoney(value, format, { currency });
  const failure = submit.error ?? adjust.error ?? unadjust.error;
  const mine = rollUp.data?.own.submission ?? null;

  return (
    <Panel data-region="forecast-submit" data-period={periodStart}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{`${t('forecast.submit.title')} — ${periodLabel}`}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('forecast.submit.body')}</p>
        </div>

        {failure ? (
          <p data-forecast-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {failure.message}
          </p>
        ) : null}

        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('forecast.submit.amount')}
            <input
              data-forecast-submit-amount
              inputMode="decimal"
              className="w-col-amount rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </label>
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-dense font-medium text-ink">
            {t('forecast.submit.note')}
            <input
              data-forecast-submit-note
              className="w-full rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
          <Button
            intent="primary"
            size="small"
            data-forecast-submit
            onClick={() => submit.mutate()}
            disabled={amount.trim() === ''}
          >
            {mine ? t('forecast.submit.resubmit') : t('forecast.submit.action')}
          </Button>
        </div>

        {/* What you last said, and what your manager did with it. Both, always
            - a rep who sees only the adjusted figure cannot tell their number
            was changed. */}
        <p data-forecast-mine className="text-meta text-ink-meta">
          {mine === null ? (
            t('forecast.submit.none')
          ) : (
            <>
              <Num data-forecast-mine-amount>{money(mine.amount)}</Num>
              {mine.adjustedAmount === null ? null : (
                <span data-forecast-mine-adjusted className="ps-2 text-warning-text">
                  {t('forecast.adjusted.by', {
                    values: {
                      amount: money(mine.adjustedAmount),
                      name: mine.adjustedByName ?? '',
                    },
                  })}
                </span>
              )}
            </>
          )}
        </p>

        {manages && rollUp.isPending ? <Skeleton className="h-block-xs w-full" /> : null}
        {manages && rollUp.data ? (
          <div data-region="forecast-rollup" className="flex flex-col gap-2 border-t border-t-rule pt-3">
            <SectionLabel>{t('forecast.rollup.title')}</SectionLabel>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-micro uppercase tracking-label text-ink-label">
                {t('forecast.rollup.total')}
              </span>
              <Num data-forecast-rollup-total className="text-h3 text-ink">
                {money(rollUp.data.total)}
              </Num>
            </div>
            {/* The line that stops the total being read as complete. */}
            {rollUp.data.awaiting > 0 ? (
              <p
                data-forecast-awaiting
                className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text"
              >
                {t('forecast.rollup.awaiting', {
                  values: { count: formatCount(rollUp.data.awaiting, format) },
                })}
              </p>
            ) : (
              <p data-forecast-complete className="text-meta text-ink-meta">
                {t('forecast.rollup.complete')}
              </p>
            )}

            <ul className="flex flex-col">
              {rollUp.data.reports.map((entry) => (
                <li
                  key={entry.userId}
                  data-forecast-report={entry.userId}
                  className="flex flex-col gap-1 border-t border-t-rule py-2 first:border-t-0"
                >
                  <div className="flex flex-wrap items-baseline gap-3">
                    <span className="min-w-0 flex-1 truncate text-body text-ink">{entry.name}</span>
                    {entry.submission === null ? (
                      <span data-forecast-report-none={entry.userId} className="text-meta text-ink-meta">
                        {t('forecast.rollup.notSubmitted')}
                      </span>
                    ) : (
                      <>
                        <Num data-forecast-report-said={entry.userId} className="text-body text-ink">
                          {money(entry.submission.amount)}
                        </Num>
                        {entry.submission.adjustedAmount === null ? null : (
                          <span
                            data-forecast-report-adjusted={entry.userId}
                            className="text-meta text-warning-text"
                          >
                            {t('forecast.adjusted.by', {
                              values: {
                                amount: money(entry.submission.adjustedAmount),
                                name: entry.submission.adjustedByName ?? '',
                              },
                            })}
                          </span>
                        )}
                        <Button
                          intent="ghost"
                          size="small"
                          data-forecast-adjust-open={entry.userId}
                          onClick={() =>
                            setAdjusting(adjusting === entry.userId ? null : entry.userId)
                          }
                        >
                          {t('forecast.adjust.title')}
                        </Button>
                        {entry.submission.adjustedAmount === null ? null : (
                          <Button
                            intent="ghost"
                            size="small"
                            data-forecast-adjust-clear={entry.userId}
                            onClick={() => unadjust.mutate(entry.userId)}
                          >
                            {t('forecast.adjust.clear')}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                  {adjusting === entry.userId ? (
                    <div data-forecast-adjust-form={entry.userId} className="flex flex-wrap items-end gap-2">
                      <label className="flex flex-col gap-1 text-dense font-medium text-ink">
                        {t('forecast.submit.amount')}
                        <input
                          data-forecast-adjust-amount
                          inputMode="decimal"
                          className="w-col-amount rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                          value={adjustAmount}
                          onChange={(event) => setAdjustAmount(event.target.value)}
                        />
                      </label>
                      <label className="flex min-w-0 flex-1 flex-col gap-1 text-dense font-medium text-ink">
                        {t('forecast.adjust.note')}
                        <input
                          data-forecast-adjust-note
                          className="w-full rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                          value={adjustNote}
                          onChange={(event) => setAdjustNote(event.target.value)}
                        />
                      </label>
                      <Button
                        intent="secondary"
                        size="small"
                        data-forecast-adjust-save={entry.userId}
                        onClick={() => adjust.mutate(entry.userId)}
                      >
                        {t('forecast.adjust.action')}
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
